/**
 * Pull human-friendly card content out of a ResolvedBuilding. The catalogue
 * stores the raw FoE entity payload on `raw`, which arrives in several
 * flavours; this parser surfaces info only where it's reliably useful:
 *
 *   - Great Buildings  →  Features card only (description, era / road
 *                         requirement, level-specific passive_bonus and
 *                         production_bonus values from the entity).
 *   - Event / special  →  Production card only.
 *                         Two sub-formats:
 *                         (a) fixed-data events (Clover Keep, Rattlebones)
 *                             store production under
 *                             `components.AllAge.production.options[]`.
 *                         (b) multi-age events (Eco Utopia, etc.) store
 *                             production under era-keyed children:
 *                             `components.{Era}.production.options[]`,
 *                             with scaled values per era. The player's
 *                             current era selects the right block; we
 *                             derive the era from the town hall's id.
 *   - Standard         →  Neither card; the panel shows only Details.
 *
 * Cards always show meaningful data or nothing.
 */
import type { ImportedCity, ResolvedBuilding } from '$lib/types'

export interface ProductionLine {
  /** Pre-formatted left-side label, e.g. "120 coins" or "Random reward". */
  label: string
  /** Right-side rate qualifier, e.g. "/ 1d" or "/ 1h". */
  rate?: string
}

export interface FeatureLine {
  text: string
}

export interface BuildingDetails {
  production: ProductionLine[]
  features: FeatureLine[]
}

const RESOURCE_LABELS: Record<string, string> = {
  money: 'coins',
  supplies: 'supplies',
  population: 'population',
  happiness: 'happiness',
  strategy_points: 'FP',
  premium: 'diamonds',
  medals: 'medals',
  goods: 'goods',
}

function fmtResource(key: string): string {
  return RESOURCE_LABELS[key] ?? key.replace(/_/g, ' ')
}

function fmtCycle(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

function fmtEra(era: string): string {
  return era.replace(/([A-Z])/g, ' $1').trim()
}

const fmtNum = (n: number): string => n.toLocaleString('en-US')

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x)
}

/**
 * Derive the player's current era from the town hall's cityentity_id.
 * The pattern is `H_{Era}_Townhall` (e.g. `H_SpaceAgeSpaceHub_Townhall`).
 * Used by extractBuildingDetails() to pick the right era block on
 * multi-age event buildings. Returns undefined if the import doesn't
 * have a town hall (rare; degraded gracefully — extractor falls back
 * to AllAge or any era).
 */
export function extractPlayerEra(importedCity: ImportedCity | null | undefined): string | undefined {
  if (!importedCity) return undefined
  const cmd = importedCity.parsed?.cityMapData
  if (!isPlainObject(cmd)) return undefined
  for (const entry of Object.values(cmd)) {
    if (!isPlainObject(entry)) continue
    if (entry.type !== 'main_building') continue
    const id = entry.cityentity_id
    if (typeof id !== 'string') continue
    const m = /^H_(.+)_Townhall$/.exec(id)
    if (m) return m[1]
  }
  return undefined
}

/**
 * Pick the components block whose production should drive the panel.
 * Order: explicit playerEra → AllAge → first era key with production.
 * Returns the era key chosen plus its components subtree, or null.
 */
function pickProductionBlock(
  components: Record<string, unknown>,
  playerEra: string | undefined,
): { era: string; block: Record<string, unknown> } | null {
  const tryBlock = (key: string) => {
    const b = components[key]
    if (!isPlainObject(b)) return null
    if (!isPlainObject(b.production)) return null
    return b
  }

  if (playerEra) {
    const b = tryBlock(playerEra)
    if (b) return { era: playerEra, block: b }
  }
  const allAge = tryBlock('AllAge')
  if (allAge) return { era: 'AllAge', block: allAge }
  for (const k of Object.keys(components)) {
    if (k === 'AllAge') continue
    const b = tryBlock(k)
    if (b) return { era: k, block: b }
  }
  return null
}

/**
 * Extract production / features from a resolved building. `level` is the
 * placed level for Great Buildings — passes through to the passive_bonus /
 * production_bonus value lookup. `playerEra` is the era string from the
 * player's town hall, used to select the right components block on
 * multi-age event buildings (e.g. Eco Utopia).
 *
 * Returns empty arrays for standard buildings so callers can hide cards
 * with `production.length === 0` / `features.length === 0` guards.
 */
export function extractBuildingDetails(
  resolved: ResolvedBuilding,
  level?: number,
  playerEra?: string,
): BuildingDetails {
  const production: ProductionLine[] = []
  const features: FeatureLine[] = []
  if (!isPlainObject(resolved.raw)) return { production, features }
  const raw = resolved.raw

  // ── Production card ── only for event / special buildings whose data
  // sits under a components block. Standard buildings ship their
  // production at the top level (`available_products`); we intentionally
  // skip those because the per-cycle lines aren't useful enough on their
  // own for a panel summary, and including them makes some buildings
  // look "rich" while others (with empty static resources) look bare
  // for no reason a player can see.
  //
  // Two formats handled here:
  //   (a) fixed-data events (Clover Keep): components.AllAge.production
  //   (b) multi-age events (Eco Utopia):   components.{Era}.production
  // pickProductionBlock chooses the right one based on playerEra.
  const components = raw.components
  if (isPlainObject(components)) {
    const picked = pickProductionBlock(components, playerEra)
    const compProduction = picked && isPlainObject(picked.block.production)
      ? picked.block.production
      : undefined
    if (compProduction && Array.isArray(compProduction.options)) {
      for (const optRaw of compProduction.options as unknown[]) {
        if (!isPlainObject(optRaw)) continue
        const opt = optRaw
        const cycle = typeof opt.time === 'number' ? `/ ${fmtCycle(opt.time)}` : undefined
        if (!Array.isArray(opt.products)) continue
        for (const pRaw of opt.products as unknown[]) {
          if (!isPlainObject(pRaw)) continue
          const p = pRaw
          if (p.type === 'resources') {
            const playerRes = p.playerResources
            const res = isPlainObject(playerRes) ? playerRes.resources : undefined
            if (isPlainObject(res)) {
              for (const [key, value] of Object.entries(res)) {
                if (typeof value === 'number' && value !== 0) {
                  production.push({
                    label: `${fmtNum(value)} ${fmtResource(key)}`,
                    rate: cycle,
                  })
                }
              }
            }
          } else if (p.type === 'random' && Array.isArray(p.products)) {
            // Multi-outcome reward — collapse to one line.
            production.push({ label: 'Random reward', rate: cycle })
          } else if (p.type === 'genericReward') {
            // Single fixed reward (e.g. unit chest, building chest). The
            // reward.id is encoded — we can't look up the human name without
            // the lookup map, so display generically.
            production.push({ label: 'Reward', rate: cycle })
          }
        }
      }
    }
  }

  // ── Features card ── only for Great Buildings. The description, era,
  // road requirement, and the level-specific passive / production bonus
  // values combine into a single useful summary the player can read on
  // hover. Non-GB buildings get nothing here — Details is enough.
  if (resolved.type === 'greatbuilding') {
    const desc = raw.description
    if (typeof desc === 'string' && desc.length > 0) {
      const trimmed = desc.length > 220 ? desc.slice(0, 217) + '…' : desc
      features.push({ text: trimmed })
    }

    // Level-specific bonus values. Index is clamped to the values array
    // length so a level beyond the catalogue's tabulated tiers reads the
    // top tier (which matches in-game behaviour for the cap).
    const lvlIdx = typeof level === 'number' && level >= 0 ? Math.floor(level) : 0
    const pb = raw.passive_bonus
    if (isPlainObject(pb) && Array.isArray(pb.values)) {
      const arr = pb.values
      const v = arr[Math.min(lvlIdx, arr.length - 1)]
      if (typeof v === 'number' && v > 0 && typeof pb.type === 'string') {
        features.push({ text: `Passive: ${fmtNum(v)} ${fmtResource(pb.type)}` })
      }
    }
    const prodB = raw.production_bonus
    if (isPlainObject(prodB) && Array.isArray(prodB.values)) {
      const arr = prodB.values
      const v = arr[Math.min(lvlIdx, arr.length - 1)]
      if (typeof v === 'number' && v > 0 && typeof prodB.type === 'string') {
        features.push({ text: `Production: ${fmtNum(v)} ${fmtResource(prodB.type)}` })
      }
    }

    if (resolved.era) features.push({ text: `Era: ${fmtEra(resolved.era)}` })
    if (resolved.needsRoad) features.push({ text: 'Needs road' })
  }

  return { production, features }
}
